import { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { Users, Navigation, MapPin, RefreshCw, Trash2 } from "lucide-react";
import L from "leaflet";
import LocationSharingButton from "@/components/LocationSharingButton";
import { useAuth } from "@/App";

// Leaflet CSS is loaded from CDN in index.html

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix for default marker icons in React Leaflet
// This fixes the issue where markers don't show up
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import iconRetina from 'leaflet/dist/images/marker-icon-2x.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  iconRetinaUrl: iconRetina,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

// Custom icons for different user states
const createCustomIcon = (color, username = '') => {
  return L.divIcon({
    className: "custom-marker",
    html: `
      <div style="position: relative;">
        <div style="
          background-color: ${color};
          width: 30px;
          height: 30px;
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          border: 3px solid white;
          box-shadow: 0 2px 5px rgba(0,0,0,0.3);
        ">
          <div style="
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            transform: rotate(45deg);
            color: white;
            font-weight: bold;
            font-size: 16px;
          ">📍</div>
        </div>
        ${username ? `
          <div style="
            position: absolute;
            top: -25px;
            left: 50%;
            transform: translateX(-50%);
            background-color: ${color};
            color: white;
            padding: 2px 8px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: bold;
            white-space: nowrap;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
            border: 1px solid white;
          ">${username}</div>
        ` : ''}
      </div>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -55],
  });
};

// Component to auto-fit map bounds to markers
function MapBoundsHandler({ locations }) {
  const map = useMap();

  useEffect(() => {
    if (locations.length > 0) {
      const bounds = L.latLngBounds(
        locations.map((loc) => [loc.latitude, loc.longitude])
      );
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
    }
  }, [locations, map]);

  return null;
}

export default function Map() {
  const { user } = useAuth();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [currentSharingCallId, setCurrentSharingCallId] = useState(null);
  
  // Default map center (New York City as fallback)
  const [mapCenter, setMapCenter] = useState([40.7128, -74.0060]);
  const [mapZoom, setMapZoom] = useState(13);

  const refreshIntervalRef = useRef(null);
  
  const isAdmin = user?.is_admin || user?.role === 'admin';

  // Clear all location tracking (admin only)
  const handleClearAllTracking = async () => {
    if (!window.confirm('Are you sure you want to clear ALL location tracking? This will stop all units from sharing their location.')) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/location/admin/clear-all-tracking`, {
        withCredentials: true
      });
      toast.success(`Cleared ${response.data.locations_cleared} locations and ${response.data.unit_tracking_cleared} tracking records`);
      fetchLocations();
    } catch (error) {
      console.error('Failed to clear tracking:', error);
      toast.error('Failed to clear tracking: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Fetch active locations
  const fetchLocations = async () => {
    try {
      console.log('Fetching all active locations (no filter)');
      
      const response = await axios.get(`${API}/location/active`, {
        withCredentials: true,
      });
      
      console.log('Received locations:', response.data);
      console.log('Location count:', response.data.locations?.length || 0);
      
      setLocations(response.data.locations || []);
      
      // If locations exist, center map on them
      if (response.data.locations.length > 0) {
        const firstLocation = response.data.locations[0];
        setMapCenter([firstLocation.latitude, firstLocation.longitude]);
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
      if (error.response?.status !== 401) {
        toast.error("Failed to fetch locations");
      }
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh effect
  useEffect(() => {
    fetchLocations();

    if (autoRefresh) {
      refreshIntervalRef.current = setInterval(() => {
        fetchLocations();
      }, 5000); // Refresh every 5 seconds
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [autoRefresh]);

  // Manual refresh
  const handleRefresh = () => {
    setLoading(true);
    fetchLocations();
  };

  // Get marker color based on accuracy
  const getMarkerColor = (accuracy) => {
    if (accuracy < 20) return "#10b981"; // Green - high accuracy
    if (accuracy < 50) return "#f59e0b"; // Orange - medium accuracy
    return "#ef4444"; // Red - low accuracy
  };

  // Format timestamp
  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString("en-US", { timeZone: "America/New_York" });
  };

  if (loading && locations.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col p-4 gap-4">
      {/* Header - lower z-index so sidebar stays on top */}
      <Card className="bg-slate-900 border-slate-800 relative z-10">
        <CardHeader className="pb-3">
          {/* Top row - responsive */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <MapPin className="w-6 h-6 text-blue-500" />
              <CardTitle className="text-xl uppercase tracking-wider">
                Live Location Map
              </CardTitle>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-4 w-full sm:w-auto relative z-10">
              {/* Location Sharing Button - Primary action */}
              <LocationSharingButton 
                liveCallId={null}
                currentSharingCallId={currentSharingCallId}
                onSharingChange={setCurrentSharingCallId}
                className="flex-shrink-0"
              />

              {/* Active users count */}
              <Badge variant="outline" className="text-sm flex-shrink-0">
                <Users className="w-4 h-4 mr-1" />
                {locations.length} Active
              </Badge>

              {/* Auto-refresh toggle */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <Switch
                  id="auto-refresh"
                  checked={autoRefresh}
                  onCheckedChange={setAutoRefresh}
                />
                <Label htmlFor="auto-refresh" className="text-sm whitespace-nowrap">
                  Auto-refresh
                </Label>
              </div>

              {/* Manual refresh button */}
              <Button
                size="sm"
                variant="outline"
                onClick={handleRefresh}
                disabled={loading}
                className="flex-shrink-0"
              >
                <RefreshCw
                  className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                />
              </Button>
              
              {/* Admin: Clear all tracking */}
              {isAdmin && locations.length > 0 && (
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={handleClearAllTracking}
                  className="flex-shrink-0"
                  title="Clear all location tracking"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Clear All
                </Button>
              )}
            </div>
          </div>
          
          {/* Helpful message */}
          {locations.length === 0 && (
            <div className="mt-3 p-2 bg-blue-950/20 border border-blue-900/50 rounded text-xs text-blue-400">
              💡 Click "Share Location" above to add your location to the map. Your location will update automatically.
            </div>
          )}
        </CardHeader>
      </Card>

      {/* Map Container - lower z-index so controls stay on top */}
      <Card className="flex-1 bg-slate-900 border-slate-800 overflow-hidden relative z-0">
        <CardContent className="p-0 h-full">
          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            style={{ height: "100%", width: "100%" }}
            className="z-0"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            <MapBoundsHandler locations={locations} />

            {locations.map((location) => (
                <Marker
                  key={location.user_id}
                  position={[location.latitude, location.longitude]}
                  icon={createCustomIcon(getMarkerColor(location.accuracy), location.username)}
                >
                  <Popup>
                    <div className="text-sm">
                      <div className="font-bold text-base mb-2">
                        {location.username}
                        {location.badge_number && ` (#${location.badge_number})`}
                      </div>
                      
                      <div className="space-y-1 text-xs">
                        <div>
                          <span className="font-semibold">Location:</span>{" "}
                          {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                        </div>
                        
                        <div>
                          <span className="font-semibold">Accuracy:</span>{" "}
                          ±{Math.round(location.accuracy)}m
                        </div>
                        
                        <div>
                          <span className="font-semibold">Last Update:</span>{" "}
                          {formatTime(location.timestamp)}
                        </div>
                        
                        {location.live_call_id && (
                          <div>
                            <span className="font-semibold">Call ID:</span>{" "}
                            {location.live_call_id}
                          </div>
                        )}
                      </div>
                      
                      {/* Accuracy indicator */}
                      <div className="mt-2 pt-2 border-t">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: getMarkerColor(location.accuracy) }}
                          ></div>
                          <span className="text-xs">
                            {location.accuracy < 20
                              ? "High Accuracy"
                              : location.accuracy < 50
                              ? "Medium Accuracy"
                              : "Low Accuracy"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
        </CardContent>
      </Card>

      {/* Active Users List */}
      {locations.length > 0 && (
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm uppercase tracking-wider">
              Active Users ({locations.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
              {locations.map((location) => (
                <div
                  key={location.user_id}
                  className="p-2 bg-slate-950 rounded border border-slate-800 text-sm"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: getMarkerColor(location.accuracy) }}
                      ></div>
                      <span className="font-semibold">{location.username}</span>
                    </div>
                    <span className="text-xs text-slate-500">
                      {formatTime(location.timestamp)}
                    </span>
                  </div>
                  {location.badge_number && (
                    <div className="text-xs text-slate-400 mt-1">
                      Badge #{location.badge_number}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
